import { useRecoilValue } from 'recoil';
import { useActiveReplanningStage } from '~/hooks/useActiveReplanningStage';
import type { ImageRoleLabel } from '~/hooks/workflows/isro/useIsroImageLabel';
import {
    replanningQaContourSetState,
    replanningRunOutputsState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { useRunScans } from './useRunScans';

// Inside adaptive replanning the run's scans are named for their role in the run, and for the sets they show.
export function useReplanningImageLabel(
    seriesUid: string | undefined
): ImageRoleLabel | null {
    const stage = useActiveReplanningStage();
    const { planning, target } = useRunScans();
    const qaContours = useRecoilValue(replanningQaContourSetState);
    const { propagatedUid } = useRecoilValue(replanningRunOutputsState);
    if (!stage || !seriesUid) {
        return null;
    }
    const inQa = stage.id === 'registration-qa';
    // QA shows this run's Contour+ sets once they are in; until then, the propagated and delivered sets.
    const showsContours =
        inQa && !!propagatedUid && qaContours?.forRun === propagatedUid;
    const showsRunSets = stage.id === 'review' || (inQa && !showsContours);
    if (seriesUid === target?.seriesUid) {
        return {
            id: showsContours
                ? 'replanning.panel.target.contour'
                : showsRunSets
                  ? 'replanning.panel.target.propagated'
                  : 'replanning.panel.target',
        };
    }
    if (seriesUid === planning?.seriesUid) {
        return {
            id: showsContours
                ? 'replanning.panel.source.contour'
                : showsRunSets
                  ? 'replanning.panel.source.delivered'
                  : 'replanning.panel.source',
        };
    }
    return null;
}
